import { Document, Page as PdfPage, Text, View, StyleSheet } from '@react-pdf/renderer';
import { registerPdfFonts } from './pdf-fonts';

registerPdfFonts();

// ─── Roster annex PDF (Camino TMAI · R0-PR6) — CONTAINS PII ──────────────────
// The SEPARATE PII-bearing artifact (spec §10): student + graduate names,
// location and (church) organization. NOT part of the aggregate packet and NOT
// publishable to the network (R1 must refuse anything with containsPii). A loud
// red header states as much. i18n-AGNOSTIC — labels resolved to English at the
// call site, same as the packet.

export interface MembershipRosterAnnexRowView {
  fullName: string;
  kindLabel: string; // English "Student" / "Graduate"
  location?: string;
  organizationName?: string;
}

export interface MembershipRosterAnnexPdfData {
  brand: { tenantName: string; primaryColor: string };
  generatedOn: string;
  ministryDataGap: boolean;
  rows: MembershipRosterAnnexRowView[];
  labels: {
    title: string;
    piiWarning: string;
    generatedOn: string;
    colName: string;
    colType: string;
    colLocation: string;
    colOrg: string;
    ministryGapNote: string;
    empty: string;
    footerDisclaimer: string;
  };
}

const styles = StyleSheet.create({
  page: {
    fontFamily: 'EB Garamond',
    fontSize: 10,
    color: '#1f2937',
    paddingTop: 42,
    paddingBottom: 56,
    paddingHorizontal: 48,
  },
  piiBanner: {
    backgroundColor: '#991b1b',
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 700,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 1,
    paddingVertical: 6,
    borderRadius: 3,
    marginBottom: 14,
  },
  header: { textAlign: 'center', marginBottom: 14 },
  tenantName: { fontSize: 15, fontWeight: 700 },
  brandBar: { width: 64, height: 3, borderRadius: 2, alignSelf: 'center', marginVertical: 6 },
  docTitle: { fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' },
  metaLabel: { fontSize: 7, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 },
  table: { marginTop: 6 },
  tr: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e2e8f0',
    paddingVertical: 4,
  },
  thRow: { borderBottomWidth: 1, borderBottomColor: '#94a3b8' },
  th: {
    fontSize: 8,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: '#475569',
  },
  td: { fontSize: 9.5, color: '#374151' },
  cName: { width: '32%', paddingRight: 4 },
  cType: { width: '16%', paddingRight: 4 },
  cLoc: { width: '28%', paddingRight: 4 },
  cOrg: { width: '24%' },
  muted: { color: '#cbd5e1' },
  gapNote: {
    marginTop: 12,
    fontSize: 8.5,
    color: '#b45309',
    fontStyle: 'italic',
    lineHeight: 1.4,
  },
  emptyNote: { fontSize: 10, color: '#94a3b8', fontStyle: 'italic', marginTop: 12 },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 48,
    right: 48,
    fontSize: 8,
    textAlign: 'center',
    color: '#94a3b8',
    borderTopWidth: 0.5,
    borderTopColor: '#e2e8f0',
    paddingTop: 6,
  },
  pageNumber: { position: 'absolute', bottom: 10, right: 48, fontSize: 8, color: '#94a3b8' },
});

export function MembershipRosterAnnexPdfDocument({
  data,
}: {
  data: MembershipRosterAnnexPdfData;
}): JSX.Element {
  const { brand, labels } = data;
  const dash = '—';
  return (
    <Document>
      <PdfPage size="A4" style={styles.page}>
        <Text style={styles.piiBanner} fixed>
          {labels.piiWarning}
        </Text>

        <View style={styles.header}>
          <Text style={styles.tenantName}>{brand.tenantName}</Text>
          <View style={[styles.brandBar, { backgroundColor: brand.primaryColor }]} />
          <Text style={styles.docTitle}>{labels.title}</Text>
          <Text style={[styles.metaLabel, { marginTop: 4 }]}>
            {labels.generatedOn}: {data.generatedOn}
          </Text>
        </View>

        {data.rows.length === 0 ? (
          <Text style={styles.emptyNote}>{labels.empty}</Text>
        ) : (
          <View style={styles.table}>
            <View style={[styles.tr, styles.thRow]} fixed>
              <Text style={[styles.th, styles.cName]}>{labels.colName}</Text>
              <Text style={[styles.th, styles.cType]}>{labels.colType}</Text>
              <Text style={[styles.th, styles.cLoc]}>{labels.colLocation}</Text>
              <Text style={[styles.th, styles.cOrg]}>{labels.colOrg}</Text>
            </View>
            {data.rows.map((r, i) => (
              <View key={i} style={styles.tr} wrap={false}>
                <Text style={[styles.td, styles.cName]}>{r.fullName}</Text>
                <Text style={[styles.td, styles.cType]}>{r.kindLabel}</Text>
                <Text style={[styles.td, styles.cLoc, r.location ? {} : styles.muted]}>
                  {r.location ?? dash}
                </Text>
                <Text style={[styles.td, styles.cOrg, r.organizationName ? {} : styles.muted]}>
                  {r.organizationName ?? dash}
                </Text>
              </View>
            ))}
          </View>
        )}

        {data.ministryDataGap ? <Text style={styles.gapNote}>{labels.ministryGapNote}</Text> : null}

        <Text style={styles.footer} fixed>
          {labels.footerDisclaimer}
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </PdfPage>
    </Document>
  );
}
